import React from "react";
import { 
  Plane, 
  Waves, 
  Mountain, 
  Zap, 
  MapPin, 
  Car, 
  Clock,
  ArrowRight 
} from "lucide-react";

const ActivitiesSection = () => {
  const activities = [
    {
      icon: Plane,
      title: "Parapente",
      description: "Sensations fortes au-dessus de la Suisse Normande",
      distance: "Sur place à Clécy"
    },
    {
      icon: Waves,
      title: "Canoë",
      description: "Descente paisible de l'Orne en pleine nature",
      distance: "5 minutes du gîte"
    },
    {
      icon: Mountain,
      title: "Via ferrata",
      description: "Parcours aventure sur les falaises normandes",
      distance: "À proximité"
    },
    {
      icon: Zap,
      title: "Luge d'été",
      description: "Plaisir garanti pour petits et grands",
      distance: "À Clécy"
    }
  ];

  const destinations = [
    {
      name: "Rochers d'Oëtre",
      description: "Point de vue spectaculaire sur la vallée",
      distance: "13 km",
      time: "15 min",
      color: "bg-green-100 text-green-800"
    },
    {
      name: "Caen",
      description: "Ville historique et châteaux normands",
      distance: "30 km", 
      time: "35 min",
      color: "bg-blue-100 text-blue-800"
    },
    {
      name: "Plages du Débarquement",
      description: "Sites historiques du D-Day",
      distance: "50 km",
      time: "1h",
      color: "bg-orange-100 text-orange-800"
    },
    {
      name: "Mont-Saint-Michel",
      description: "Merveille architecturale de Normandie",
      distance: "80 km",
      time: "1h20",
      color: "bg-purple-100 text-purple-800"
    }
  ];

  return (
    <section 
      id="activities" 
      className="section-padding bg-white"
      aria-labelledby="activities-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            id="activities-title"
            className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Activités à proximité
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Découvrez les trésors de la Suisse Normande et ses environs
          </p>
        </div>

        {/* Activités locales */}
        <div className="mb-16">
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1C3A27] mb-8 text-center">
            Activités sportives à Clécy
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {activities.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-xl p-6 shadow-lg border border-[#EDE6DB]/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#1C3A27]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1C3A27] transition-colors duration-300">
                      <IconComponent 
                        size={32} 
                        className="text-[#1C3A27] group-hover:text-white transition-colors duration-300"
                      />
                    </div>
                    
                    <h4 className="font-semibold text-lg text-[#1C3A27] mb-2">
                      {activity.title}
                    </h4>
                    
                    <p className="text-[#1C3A27]/70 text-sm mb-3 leading-relaxed">
                      {activity.description}
                    </p>
                    
                    <div className="flex items-center justify-center text-xs text-[#1C3A27]/60">
                      <MapPin size={14} className="mr-1" />
                      {activity.distance}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Destinations - En cartes */}
        <div>
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1C3A27] mb-8 text-center">
            Destinations incontournables
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {destinations.map((destination, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-white to-[#FAF9F6] rounded-xl p-6 shadow-lg border border-[#EDE6DB]/50 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="font-serif text-xl font-bold text-[#1C3A27] mb-2">
                      {destination.name}
                    </h4>
                    <p className="text-[#1C3A27]/70 text-sm leading-relaxed mb-3">
                      {destination.description}
                    </p>
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="text-[#1C3A27]/40 group-hover:text-[#1C3A27] transition-colors flex-shrink-0 ml-4"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center text-sm text-[#1C3A27]/80">
                      <Car size={16} className="mr-1" />
                      {destination.distance}
                    </div>
                    <div className="flex items-center text-sm text-[#1C3A27]/80">
                      <Clock size={16} className="mr-1" />
                      {destination.time}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${destination.color}`}>
                    Recommandé
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Information complémentaire */}
        <div className="mt-12 text-center">
          <div className="bg-[#EDE6DB]/30 rounded-2xl p-6 md:p-8 max-w-4xl mx-auto">
            <h4 className="font-serif text-xl font-bold text-[#1C3A27] mb-4">
              Position idéale pour explorer la Normandie
            </h4>
            <p className="text-[#1C3A27]/80 leading-relaxed">
              Depuis Clécy, vous êtes au cœur de la Suisse Normande avec un accès facile aux plus beaux sites 
              de la région. Que vous préfériez les activités sportives, les visites culturelles ou les 
              découvertes gastronomiques, tout est à portée de main.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivitiesSection;